import type { DashboardMetricsResponse } from '../../../types/dashboard';
import { toDemographicCounts } from '../demographics';
import type { ThecData } from './types';
import { TULSA_POPULATION_PCT } from './types';
import { MAX_QUARTERS_CHART } from '../constants';

export function latest<T>(arr: T[]): T {
  return arr[arr.length - 1];
}

export function previous<T>(arr: T[]): T | null {
  return arr.length >= 2 ? arr[arr.length - 2] : null;
}

export function pctChange(current: number, prevVal: number | null): number | null {
  if (prevVal === null || prevVal === 0) return null;
  return ((current - prevVal) / prevVal) * 100;
}

const asNumber = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0;

export function mapApiToThecData(data: DashboardMetricsResponse | null | undefined): ThecData | null {
  if (!data) return null;
  const section = data.sections.find((entry) => entry.sectionId === 'thec');
  if (!section) return null;

  const byId = new Map(section.metrics.map((metric) => [metric.metricId, metric]));

  const participants = byId.get('thec.participants');
  const completions = byId.get('thec.completions');
  const credit = byId.get('thec.academicCredit');
  const employer = byId.get('thec.employerSatisfaction');
  const participant = byId.get('thec.participantSatisfaction');
  const demographics = byId.get('thec.demographics');
  const placements = byId.get('thec.placements');

  const mapPoint = (p: Record<string, unknown>) => {
    return {
      byRace: toDemographicCounts(p.byRace),
      byGender: toDemographicCounts(p.byGender),
    };
  };

  return {
    participants: (participants?.series ?? []).slice(-MAX_QUARTERS_CHART).map((p) => ({
      quarter: String(p.quarter),
      participantsStarted: asNumber(p.participantsStarted),
      qoq: (p.qoq ?? {}) as Record<string, unknown>,
      yoy: (p.yoy ?? {}) as Record<string, unknown>,
    })) ?? [],
    participantsAllTimeTotal: participants ? asNumber(participants.allTime?.participantsStarted) : 0,
    completions: (completions?.series ?? []).slice(-MAX_QUARTERS_CHART).map((p) => ({
      quarter: String(p.quarter),
      completed: asNumber(p.completed),
      qoq: (p.qoq ?? {}) as Record<string, unknown>,
      yoy: (p.yoy ?? {}) as Record<string, unknown>,
    })) ?? [],
    completionsAllTimeTotal: completions ? asNumber(completions.allTime?.completed) : 0,
    academicCredit: (credit?.series ?? []).slice(-MAX_QUARTERS_CHART).map((p) => ({
      quarter: String(p.quarter),
      creditYes: asNumber(p.creditYes),
      creditNo: asNumber(p.creditNo),
      qoq: (p.qoq ?? {}) as Record<string, unknown>,
      yoy: (p.yoy ?? {}) as Record<string, unknown>,
    })) ?? [],
    academicCreditAllTime: credit
      ? {
          creditYes: asNumber(credit.allTime?.creditYes),
          creditNo: asNumber(credit.allTime?.creditNo),
        }
      : { creditYes: 0, creditNo: 0 },
    employerSatisfaction: (employer?.series ?? []).slice(-MAX_QUARTERS_CHART).map((p) => ({
      quarter: String(p.quarter),
      percentSatisfied: asNumber(p.percentSatisfied),
      qoq: (p.qoq ?? {}) as Record<string, unknown>,
      yoy: (p.yoy ?? {}) as Record<string, unknown>,
    })) ?? [],
    employerSatisfactionAllTimeAvg: employer ? asNumber(employer.allTime?.percentSatisfied) : 0,
    participantSatisfaction: (participant?.series ?? []).slice(-MAX_QUARTERS_CHART).map((p) => ({
      quarter: String(p.quarter),
      percentSatisfied: asNumber(p.percentSatisfied),
      qoq: (p.qoq ?? {}) as Record<string, unknown>,
      yoy: (p.yoy ?? {}) as Record<string, unknown>,
    })) ?? [],
    participantSatisfactionAllTimeAvg: participant ? asNumber(participant.allTime?.percentSatisfied) : 0,
    demographics: (demographics?.series ?? []).slice(-MAX_QUARTERS_CHART).map((p) => ({
      quarter: String(p.quarter),
      ...mapPoint(p),
      qoq: (p.qoq ?? {}) as Record<string, unknown>,
      yoy: (p.yoy ?? {}) as Record<string, unknown>,
    })) ?? [],
    demographicsAllTime: demographics
      ? {
          byRace: toDemographicCounts(demographics.allTime?.byRace),
          byGender: toDemographicCounts(demographics.allTime?.byGender),
        }
      : { byRace: {}, byGender: {} },
    placements: (placements?.series ?? []).slice(-MAX_QUARTERS_CHART).map((p) => ({
      quarter: String(p.quarter),
      placedInTulsa: asNumber(p.placedInTulsa),
      qoq: (p.qoq ?? {}) as Record<string, unknown>,
      yoy: (p.yoy ?? {}) as Record<string, unknown>,
    })) ?? [],
    placementsAllTimeTotal: placements ? asNumber(placements.allTime?.placedInTulsa) : 0,
    tulsaPopulationPct: { ...TULSA_POPULATION_PCT },
  };
}
